import { useCallback, useEffect, useRef, useState } from 'react';
import { Mic } from '../primitives';
import { useVoiceToText } from '../../lib/voiceInput';
import { supabase } from '../../lib/supabase';
import { glassModal, glassInput } from './liquidGlass';

interface NotesAndClipPanelProps {
  open: boolean;
  onClose: () => void;
  drillId: string | null | undefined;
  /** Used to seed Brain auto-draft prompts with the live spec. */
  onAiDraftOrganisation?: () => void;
  onAiDraftCoachingPoints?: () => void;
}

interface ReferenceClip {
  url: string;
  ts_in: number;
  ts_out: number;
  note: string;
}

/**
 * Drawer with the per-drill notes + a reference video clip pointer.
 * web1 parity — coaches need to capture organisation steps + coaching
 * points + a tag back to a real clip when the drill was inspired by
 * something they saw.
 */
export function NotesAndClipPanel({
  open, onClose, drillId, onAiDraftOrganisation, onAiDraftCoachingPoints,
}: NotesAndClipPanelProps): React.JSX.Element | null {
  const [orgMd, setOrgMd] = useState('');
  const [coachMd, setCoachMd] = useState('');
  const [clip, setClip] = useState<ReferenceClip>({ url: '', ts_in: 0, ts_out: 0, note: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !drillId) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error } = await supabase
        .from('drills')
        .select('organisation_md, coaching_points_md, reference_clip')
        .eq('id', drillId)
        .maybeSingle();
      if (cancelled) return;
      setLoading(false);
      if (error || !data) return;
      const r = data as Record<string, unknown>;
      setOrgMd((r.organisation_md as string) ?? '');
      setCoachMd((r.coaching_points_md as string) ?? '');
      const rc = r.reference_clip as ReferenceClip | null;
      setClip(rc ?? { url: '', ts_in: 0, ts_out: 0, note: '' });
    })();
    return () => { cancelled = true; };
  }, [open, drillId]);

  // Voice: track which markdown field is focused.
  const voiceTargetRef = useRef<'org' | 'coach' | 'clip_note' | null>(null);
  const lastVoiceLenRef = useRef(0);
  const voice = useVoiceToText({
    onTranscript: (text) => {
      const delta = text.slice(lastVoiceLenRef.current);
      lastVoiceLenRef.current = text.length;
      if (!delta.trim()) return;
      const append = (current: string) => (current ? `${current} ${delta.trim()}` : delta.trim());
      if (voiceTargetRef.current === 'org') setOrgMd((p) => append(p));
      else if (voiceTargetRef.current === 'coach') setCoachMd((p) => append(p));
      else if (voiceTargetRef.current === 'clip_note') setClip((p) => ({ ...p, note: append(p.note) }));
    },
  });
  useEffect(() => {
    if (!voice.listening) lastVoiceLenRef.current = 0;
  }, [voice.listening]);

  const handleSave = useCallback(async () => {
    if (!drillId || saving) return;
    setSaving(true);
    setMsg(null);
    const referenceClip = clip.url.trim()
      ? { url: clip.url.trim(), ts_in: clip.ts_in || 0, ts_out: clip.ts_out || 0, note: clip.note.trim() }
      : null;
    const { error } = await supabase.from('drills')
      .update({
        organisation_md: orgMd.trim() || null,
        coaching_points_md: coachMd.trim() || null,
        reference_clip: referenceClip,
      })
      .eq('id', drillId);
    setSaving(false);
    if (error) {
      setMsg(`Save failed: ${error.message}`);
      return;
    }
    setMsg('✓ Notes saved.');
    window.setTimeout(() => setMsg(null), 1800);
  }, [drillId, orgMd, coachMd, clip, saving]);

  if (!open) return null;

  return (
    <aside role="dialog" aria-label="Notes + reference clip" style={drawerStyle}>
      <header style={headerStyle}>
        <div style={{ flex: 1 }}>
          <div style={eyebrowStyle}>Notes + clip</div>
          <h2 style={{ margin: 0, fontSize: 18 }}>Drill notes</h2>
        </div>
        <button type="button" onClick={onClose} style={closeBtnStyle} aria-label="Close">✕</button>
      </header>

      <div style={{ overflowY: 'auto', flex: 1, padding: 14 }}>
        {loading && <p style={{ textAlign: 'center', opacity: 0.7, padding: 16 }}>Loading…</p>}
        {!loading && (
          <>
            <section style={sectionStyle}>
              <div style={fieldLabelStyle}>
                <span>Organisation</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {onAiDraftOrganisation && (
                    <button type="button" onClick={onAiDraftOrganisation} style={aiBtn} title="Ask Brain to draft organisation notes from the current canvas">
                      ✨ Draft
                    </button>
                  )}
                  <Mic listening={voice.listening} mode="toggle" onStart={() => void voice.start()} onStop={voice.stop} />
                </div>
              </div>
              <textarea
                value={orgMd}
                rows={6}
                placeholder="How the drill is set up. Markdown OK."
                onChange={(e) => setOrgMd(e.target.value)}
                onFocus={() => { voiceTargetRef.current = 'org'; }}
                style={textareaStyle}
              />
            </section>

            <section style={sectionStyle}>
              <div style={fieldLabelStyle}>
                <span>Coaching points</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {onAiDraftCoachingPoints && (
                    <button type="button" onClick={onAiDraftCoachingPoints} style={aiBtn} title="Ask Brain to draft coaching points from the current canvas">
                      ✨ Draft
                    </button>
                  )}
                  <Mic listening={voice.listening} mode="toggle" onStart={() => void voice.start()} onStop={voice.stop} />
                </div>
              </div>
              <textarea
                value={coachMd}
                rows={8}
                placeholder="What to observe + coach during the drill. Markdown OK; bullet lists work well."
                onChange={(e) => setCoachMd(e.target.value)}
                onFocus={() => { voiceTargetRef.current = 'coach'; }}
                style={textareaStyle}
              />
            </section>

            <section style={sectionStyle}>
              <h3 style={sectionTitleStyle}>Reference clip</h3>
              <label style={subLabelStyle}>
                <span>Video URL</span>
                <input
                  type="url"
                  placeholder="https://… (Veo / YouTube / Hudl)"
                  value={clip.url}
                  onChange={(e) => setClip({ ...clip, url: e.target.value })}
                  style={inputStyle}
                />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={subLabelStyle}>
                  <span>Start (sec)</span>
                  <input
                    type="number"
                    value={clip.ts_in || ''}
                    onChange={(e) => setClip({ ...clip, ts_in: Number(e.target.value) || 0 })}
                    style={inputStyle}
                  />
                </label>
                <label style={subLabelStyle}>
                  <span>End (sec)</span>
                  <input
                    type="number"
                    value={clip.ts_out || ''}
                    onChange={(e) => setClip({ ...clip, ts_out: Number(e.target.value) || 0 })}
                    style={inputStyle}
                  />
                </label>
              </div>
              <label style={subLabelStyle}>
                <span style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  Clip note
                  <Mic listening={voice.listening} mode="toggle" onStart={() => void voice.start()} onStop={voice.stop} />
                </span>
                <textarea
                  rows={3}
                  value={clip.note}
                  placeholder="Why this clip — what the drill is replicating"
                  onChange={(e) => setClip({ ...clip, note: e.target.value })}
                  onFocus={() => { voiceTargetRef.current = 'clip_note'; }}
                  style={textareaStyle}
                />
              </label>
            </section>
          </>
        )}
      </div>

      <footer style={footerStyle}>
        {msg && <span style={{ color: msg.startsWith('Save failed') ? '#c0392b' : '#27ae60', fontSize: 12 }}>{msg}</span>}
        <span style={{ flex: 1 }} />
        <button type="button" onClick={() => void handleSave()} disabled={saving} style={saveBtnStyle}>
          {saving ? 'Saving…' : '💾 Save notes'}
        </button>
      </footer>
    </aside>
  );
}

// ---- styles ---------------------------------------------------------------
const drawerStyle: React.CSSProperties = {
  ...glassModal,
  position: 'fixed', top: 0, right: 0, bottom: 0,
  width: 'min(440px, 100vw)',
  borderRadius: 0,
  borderTop: 0, borderRight: 0, borderBottom: 0,
  display: 'flex', flexDirection: 'column',
  zIndex: 999, fontFamily: 'system-ui, sans-serif',
};
const headerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '12px 14px',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
};
const eyebrowStyle: React.CSSProperties = { fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase', opacity: 0.55 };
const closeBtnStyle: React.CSSProperties = {
  background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: 6, width: 32, height: 32, cursor: 'pointer',
};
const sectionStyle: React.CSSProperties = { marginBottom: 18, display: 'flex', flexDirection: 'column', gap: 8 };
const sectionTitleStyle: React.CSSProperties = {
  margin: '0 0 4px', fontSize: 13, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: 0.4,
  color: 'rgba(255,255,255,0.7)',
  borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 4,
};
const fieldLabelStyle: React.CSSProperties = {
  fontSize: 11, opacity: 0.85,
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  textTransform: 'uppercase', letterSpacing: 0.4,
};
const subLabelStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4,
  fontSize: 11, opacity: 0.85, letterSpacing: 0.4, textTransform: 'uppercase',
};
const textareaStyle: React.CSSProperties = {
  ...glassInput,
  padding: 10,
  lineHeight: 1.4,
  resize: 'vertical',
};
const inputStyle: React.CSSProperties = glassInput;
const footerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '12px 14px',
  borderTop: '1px solid rgba(255,255,255,0.1)',
};
const saveBtnStyle: React.CSSProperties = {
  padding: '8px 16px', background: '#27ae60',
  color: '#fff', border: 'none', borderRadius: 6,
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const aiBtn: React.CSSProperties = {
  background: 'rgba(39,174,96,0.15)', color: '#27ae60',
  border: '1px solid rgba(39,174,96,0.4)', borderRadius: 4,
  padding: '2px 8px', fontSize: 10, fontWeight: 600,
  cursor: 'pointer',
};
